import { TypedDocumentNode, gql } from '@apollo/client';
import { FetchMoreFunction } from '@apollo/client/react/hooks/useSuspenseQuery';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import {
  Outlet,
  generatePath,
  useNavigate,
  useOutletContext,
} from 'react-router-dom';
import styled from 'styled-components';

import { useTypedParams } from '@sorare/routing';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import IconButton from '@sorare/core/src/atoms/buttons/IconButton';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import LoadingIndicator from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { Text16, Title3 } from '@sorare/core/src/atoms/typography';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import Avatar from '@sorare/core/src/components/user/Avatar';
import { MyClubLink } from '@sorare/core/src/components/user/MyClubLink';
import {
  NBA_LEAGUES_LEAGUESLUG_LEADERBOARDS,
  NBA_LEAGUES_LEAGUESLUG_MEMBERS,
} from '@sorare/core/src/constants/__generated__/routes';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import { glossary } from '@sorare/core/src/lib/glossary';

import { LeagueMembers } from '@sorare/us-sports/src/components/leagues/LeagueMembers';
import { convertToAvatarUser } from '@sorare/us-sports/src/lib/sports';

import {
  NBALeaguesSlugQuery,
  NBALeaguesSlugQueryVariables,
} from './__generated__/layout.graphql';
import { LayoutParams } from './__generated__/routeParams';

const NBA_LEAGUES_SLUG_QUERY = gql`
  query NBALeaguesSlugQuery($leagueSlug: String!, $offset: Int!) {
    league(slug: $leagueSlug) {
      slug
      affiliate {
        id
        slug
        nickname
        suspended
        avatarUrl
      }
      ...LeagueMembers_League
    }
    currentUser {
      slug
      # eslint-disable-next-line @graphql-eslint/no-deprecated
      leagues(sport: NBA) {
        slug
      }
    }
  }
  ${
    // eslint-disable-next-line sorare/no-detached-fragments
    LeagueMembers.fragments.League
  }
` as TypedDocumentNode<NBALeaguesSlugQuery, NBALeaguesSlugQueryVariables>;

const Wrapper = styled.div`
  margin: var(--quadruple-unit);
`;

const AffiliateLink = styled(MyClubLink)`
  display: inline-flex;
  gap: var(--unit);
  align-items: center;
`;

const ContainerWithPadding = styled(FullWidthContainer)`
  padding-top: var(--intermediate-unit);
`;

const StyledTabBar = styled(TabBar)`
  justify-self: start;
  margin-bottom: var(--intermediate-unit);
`;

type LayoutData = {
  league: Exclude<NBALeaguesSlugQuery['league'], null>;
  isMember: boolean;
  fetchMore: FetchMoreFunction<
    NBALeaguesSlugQuery,
    NBALeaguesSlugQueryVariables
  >;
};

export const useLayoutData = () => useOutletContext<LayoutData>();

const LeaguesSlugPage = () => {
  const { leagueSlug } = useTypedParams<LayoutParams>();
  const navigate = useNavigate();
  const { formatMessage } = useIntlContext();

  const { data, loading, fetchMore } = useQuery(NBA_LEAGUES_SLUG_QUERY, {
    variables: {
      leagueSlug: leagueSlug || '',
      offset: 0,
    },
    skip: !leagueSlug,
  });

  const league = data?.league;

  if (loading || !league) {
    return (
      <Wrapper>
        <LoadingIndicator fullHeight />
      </Wrapper>
    );
  }

  const leaderboardsPath = generatePath(NBA_LEAGUES_LEAGUESLUG_LEADERBOARDS, {
    leagueSlug,
  });
  const membersPath = generatePath(NBA_LEAGUES_LEAGUESLUG_MEMBERS, {
    leagueSlug,
  });

  return (
    <ContainerWithPadding>
      <PageHeader>
        <Horizontal>
          <IconButton
            color="opa"
            icon={faChevronLeft}
            onClick={() => navigate('..')}
            aria-label={formatMessage(glossary.back)}
          />
          <header>
            <Title3 as="h1">{league.name}</Title3>
            {league.affiliate && (
              <AffiliateLink user={league.affiliate} sport={Sport.NBA}>
                <Avatar user={convertToAvatarUser(league.affiliate)} />
                <Text16>{league.affiliate?.nickname}</Text16>
              </AffiliateLink>
            )}
          </header>
        </Horizontal>
      </PageHeader>

      <StyledTabBar>
        <Tab to={leaderboardsPath}>
          <FormattedMessage
            id="USSports.League.Tab.leaderboardsTab"
            defaultMessage="Leaderboards"
          />
        </Tab>
        <Tab to={membersPath}>
          <FormattedMessage
            id="USSports.League.Tab.membersTab"
            defaultMessage="Members"
          />
        </Tab>
      </StyledTabBar>
      <Outlet
        context={
          {
            league,
            isMember:
              data.currentUser?.leagues
                .map(l => l.slug)
                .includes(league.slug) ?? false,
            fetchMore,
          } satisfies LayoutData
        }
      />
    </ContainerWithPadding>
  );
};

export default LeaguesSlugPage;
